"use client";
/* eslint-disable @next/next/no-img-element */

import Link from "next/link";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper/types";
import { A11y, Keyboard } from "swiper/modules";
import "swiper/css";

import { getStrapiMedia } from "../utils/api-helpers";

type SliderContext = "fleetPage" | "homepageFleet" | "homepageServices";

interface SliderCAProps {
  array: any;
  context: SliderContext;
}

const sliderButtonBaseClasses =
  "button absolute group -translate-y-1/2 z-10 h-3 w-2 disabled:opacity-40 disabled:cursor-default";

const buttonSvg = (
  <svg
    width="32"
    height="48"
    viewBox="0 0 32 48"
    className="h-full w-auto"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g filter="url(#filter0_b_1326_528)">
      <rect width="32" height="48" rx="8" fill="#F0EDE2" />
    </g>
    <path
      className="group-hover:fill-blue"
      d="M22.3455 22.7205C23.2182 23.3602 23.2182 24.6398 22.3455 25.2795L13.6182 31.6772C12.5394 32.468 11 31.7157 11 30.3977L11 17.6023C11 16.2843 12.5394 15.532 13.6182 16.3228L22.3455 22.7205Z"
      fill="#161616"
    />
    <defs>
      <filter
        id="filter0_b_1326_528"
        x="-8"
        y="-8"
        width="48"
        height="64"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feGaussianBlur in="BackgroundImageFix" stdDeviation="4" />
        <feComposite
          in2="SourceAlpha"
          operator="in"
          result="effect1_backgroundBlur_1326_528"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_backgroundBlur_1326_528"
          result="shape"
        />
      </filter>
    </defs>
  </svg>
);

function FleetPageSlide({ card }: { card: any }) {
  const imageAttributes = card?.image?.data?.attributes;
  const imageUrl = imageAttributes?.url
    ? getStrapiMedia(imageAttributes.url)
    : null;
  const [imageStatus, setImageStatus] = useState<"loading" | "ready" | "error">(
    () => (imageUrl ? "loading" : "error")
  );

  useEffect(() => {
    setImageStatus(imageUrl ? "loading" : "error");
  }, [imageUrl]);

  if (!card) {
    return null;
  }

  const listContent = typeof card?.list === "string" ? card.list : "";
  const parsedListItems = listContent
    .split(/\r?\n+/)
    .map((item: string) =>
      item
        .replace(/^[\s]*([-•*.·]+\s*)+/, "")
        .replace(/^\.+/, "")
        .trim()
    )
    .filter(Boolean);
  const hasParsedList = parsedListItems.length > 0;
  const showSkeleton = imageStatus === "loading";
  const showError = imageStatus === "error";
  const imageAlt = imageAttributes?.alternativeText || card?.title || "Fleet slide";
  const imageContent = (
    <div className="fleet-slide-media relative w-full h-[22rem] dt:h-[clamp(22rem,30vw,32rem)] dt:max-h-[32rem] overflow-hidden bg-[#fff] rounded-1/2 transition-all duration-200">
      {showSkeleton && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 bg-[#f5f1e7] text-[#a49882] animate-pulse">
          <span className="sr-only">Loading vehicle image</span>
          <div
            aria-hidden="true"
            className="h-8 w-8 rounded-full border-2 border-[#d6cfc0] border-t-transparent animate-spin"
          />
          <span aria-hidden="true" className="text-3/4 uppercase tracking-[0.35em]">
            Loading
          </span>
        </div>
      )}
      {showError && !showSkeleton && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#f5f1e7] text-[#9d9279] text-3/4 uppercase tracking-[0.3em]">
          <span>Image unavailable</span>
        </div>
      )}
      {imageUrl && (
        <Image
          src={imageUrl}
          alt={imageAlt}
          fill
          className={`object-contain object-center transition-opacity duration-300 ${imageStatus === "ready" ? "opacity-100" : "opacity-0"}`}
          sizes="(min-width: 1024px) 640px, 100vw"
          priority={false}
          draggable={false}
          onLoad={() => setImageStatus("ready")}
          onError={() => setImageStatus("error")}
        />
      )}
    </div>
  );

  return (
    <div className="text-center w-full dt:max-w-[58rem] mx-auto">
      {card.linkPage ? (
        <Link
          href={card.linkPage}
          aria-label={`View ${card.title}`}
          className="group block rounded-1/2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-4 focus-visible:ring-offset-beige"
        >
          <div className="group-hover:opacity-90 group-focus-visible:opacity-90">
            {imageContent}
          </div>
        </Link>
      ) : (
        imageContent
      )}
      <div className="fleet-slide-body mt-1 dt:mt-2 w-full px-2 dt:px-4 pb-1 dt:pb-2 text-left">
        <h3 className="text-1.5 leading-1.5 dt:text-3 dt:leading-3 uppercase font-bold tracking-wider">
          {card.linkPage ? (<Link href={card.linkPage}>{card.title}</Link>) : card.title}
        </h3>
        <div className="mt-1 dt:mt-2 dt:flex dt:gap-4 dt:items-start">
          <div className="w-full dt:w-[56%]">
            <Markdown
              remarkPlugins={[remarkGfm]}
              className="fleet-slide-text text-1 leading-1 dt:text-1.5 dt:leading-[1.8rem]"
            >
              {card?.text ?? ""}
            </Markdown>
          </div>
          <div className="mt-1 dt:mt-0 w-full dt:w-[44%]">
            {hasParsedList ? (
              <ul className="fleet-bullet-list text-1 leading-1 dt:text-1.5 dt:leading-[1.9rem]">
                {parsedListItems.map((item: string, idx: number) => (
                  <li key={`fleet-list-${card?.id ?? card?.title ?? idx}-${idx}`}>{item}</li>
                ))}
              </ul>
            ) : (
              <Markdown remarkPlugins={[remarkGfm]} className="text-1 leading-1 dt:text-1.5 dt:leading-[1.9rem]">
                {card?.list ?? ""}
              </Markdown>
            )}
          </div>
        </div>
        <div className="fleet-slide-cta hidden dt:flex flex-col dt:flex-row dt:items-center gap-1 dt:gap-2 mt-1 dt:mt-2 text-left">
          <Link
            href="/get-quote"
            className="bg-red hover:bg-blue text-white px-1.5 py-1 rounded-1/2 uppercase font-bold text-1 leading-1 tracking-wider"
          >
            Get quote
          </Link>
          <span className="tracking-wider uppercase text-red text-3/4 dt:text-1 leading-3/4 dt:leading-1">
            or call{" "}
            <a className="font-bold" href="tel: (888) 612-1308">
              (888) 612-1308
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}

function renderHomepageFleetSlide(card: any) {
  if (!card) {
    return null;
  }

  const imageAttributes = card?.image?.data?.attributes;
  const imageUrl = imageAttributes?.url
    ? getStrapiMedia(imageAttributes.url)
    : null;

  return (
    <div className="bg-[#fff] dt:rounded-1/2 fw-util-2 overflow-hidden relative mx-auto w-full">
      {imageUrl && (
        <img
          loading="lazy"
          src={imageUrl}
          alt={imageAttributes?.alternativeText || card?.textA || "Fleet image"}
          className="h-auto w-full absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          draggable={false}
        />
      )}
      <div className="absolute bottom-1 left-1 uppercase text-1.5 leading-1.5 font-bold font-breit">
        {card?.textA}
      </div>
      <div className="absolute bottom-1 right-1 uppercase text-1.5 leading-1.5">
        {card?.textB}
      </div>
    </div>
  );
}

function renderHomepageServicesSlide(card: any) {
  if (!card) {
    return null;
  }

  const imageAttributes = card?.image?.data?.attributes;
  const imageUrl = imageAttributes?.url
    ? getStrapiMedia(imageAttributes.url)
    : null;

  return (
    <div className="relative h-[15rem] dt:h-[24rem]">
      <div className="bg-white dt:rounded-1/2 overflow-hidden relative h-full">
        {imageUrl && (
          <Image
            src={imageUrl}
            alt={card?.textA || "Service image"}
            fill
            className="rounded-1/2 object-cover"
            sizes="(min-width: 1024px) 480px, 100vw"
            priority={false}
            draggable={false}
          />
        )}
      </div>
      {card?.slideLink && (
        <a
          href={card.slideLink}
          className="bg-white text-3/4 dt:text-1.5 leading-3/4 dt:leading-1.5 tracking-wider rounded-1/2 absolute -bottom-1 md:-bottom-[0.35rem] dt:bottom-auto dt:top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-1/2 md:px-1 md:py-3/4 dt:px-2 font-breit uppercase font-bold hover:text-blue"
        >
          {card?.textA}
        </a>
      )}
    </div>
  );
}


export default function SliderCA({ array, context }: SliderCAProps) {
  const slides = useMemo(() => (Array.isArray(array) ? array : []), [array]);
  const slideCount = slides.length;
  const isFleet = context === "fleetPage";
  const canNavigate = slideCount > 1;
  const enableLoop = canNavigate;
  const [swiperInstance, setSwiperInstance] = useState<SwiperInstance | null>(null);

  useEffect(() => {
    if (!swiperInstance) {
      return;
    }
    if (enableLoop && typeof swiperInstance.slideToLoop === "function") {
      swiperInstance.slideToLoop(0, 0, false);
    } else {
      swiperInstance.slideTo(0, 0, false);
    }
  }, [enableLoop, swiperInstance]);

  const goPrevious = useCallback(() => {
    swiperInstance?.slidePrev();
  }, [swiperInstance]);

  const goNext = useCallback(() => {
    swiperInstance?.slideNext();
  }, [swiperInstance]);

  const handleSwiperInit = useCallback((instance: SwiperInstance) => {
    setSwiperInstance(instance);
  }, []);


  const fleetSwiperConfig = useMemo(() => {
    if (!isFleet) {
      return null;
    }
    if (!canNavigate) {
      return {
        slidesPerView: 1,
        spaceBetween: 0,
        centeredSlides: false,
      };
    }

    return {
      slidesPerView: 1.4,
      spaceBetween: 0,
      centeredSlides: true,
      breakpoints: {
        768: {
          slidesPerView: 1.5,
          spaceBetween: 0,
        },
        1024: {
          slidesPerView: 1.65,
          spaceBetween: 0,
        },
        1280: {
          slidesPerView: 1.85,
          spaceBetween: 0,
        },
        1440: {
          slidesPerView: 2,
          spaceBetween: 18,
        },
        1920: {
          slidesPerView: 2.2,
          spaceBetween: 20,
        },
      },
    };
  }, [canNavigate, isFleet]);

  const renderSlideContent = useCallback(
    (slide: any) => {
      switch (context) {
        case "fleetPage":
          return <FleetPageSlide card={slide} />;
        case "homepageFleet":
          return renderHomepageFleetSlide(slide);
        case "homepageServices":
        default:
          return renderHomepageServicesSlide(slide);
      }
    },
    [context]
  );

  const wrapperClasses =
    isFleet
      ? "hidden dt:block w-full"
      : context === "homepageFleet"
        ? "mx-auto w-full max-w-[21.5rem] dt:max-w-full"
        : "w-full";

  const fleetNavVerticalClasses = "top-[8rem] dt:top-[clamp(9rem,12vw,13rem)]";

  const prevButtonClasses =
    isFleet
      ? `${fleetNavVerticalClasses} left-1 md:left-2 dt:left-[2rem]`
      : context === "homepageFleet"
        ? "left-[.5rem] md:left-[-1rem] dt:left-1 top-1/2"
        : "left-1 md:-left-1 dt:left-1 top-1/2";

  const nextButtonClasses =
    isFleet
      ? `${fleetNavVerticalClasses} right-1 md:right-2 dt:right-[2rem]`
      : context === "homepageFleet"
        ? "right-[.5rem] md:right-[-1rem] dt:right-1 top-1/2"
        : "right-1 md:-right-1 dt:right-1 top-1/2";

  const bodyWrapperClasses =
    isFleet
      ? "w-full mx-auto h-auto overflow-visible"
      : context === "homepageFleet"
        ? "mx-auto w-full max-w-[21.5rem] dt:max-w-full overflow-hidden"
        : "w-full mx-auto overflow-hidden";

  if (!slideCount) {
    return null;
  }

  const swiperClassName =
    context === "fleetPage"
      ? "fleet-swiper"
      : context === "homepageFleet"
        ? "homepage-fleet-swiper"
        : "homepage-services-swiper";

  return (
    <div
      className={`${wrapperClasses} relative overflow-x-hidden md:overflow-visible dt:overflow-visible`}
    >
      <button
        aria-label="Go back"
        className={`${sliderButtonBaseClasses} ${prevButtonClasses}`}
        onClick={goPrevious}
        type="button"
        disabled={!canNavigate}
      >
        <span className="sr-only">Previous slide</span>
        <span className="rotate-180 block">{buttonSvg}</span>
      </button>
      <button
        aria-label="Go forward"
        className={`${sliderButtonBaseClasses} ${nextButtonClasses}`}
        onClick={goNext}
        type="button"
        disabled={!canNavigate}
      >
        <span className="sr-only">Next slide</span>
        <span className="block">{buttonSvg}</span>
      </button>

      <div className={`${bodyWrapperClasses} relative`}>
        <Swiper
          modules={[A11y, Keyboard]}
          slidesPerView={fleetSwiperConfig?.slidesPerView ?? 1}
          spaceBetween={fleetSwiperConfig?.spaceBetween ?? 16}
          centeredSlides={fleetSwiperConfig?.centeredSlides ?? false}
          breakpoints={fleetSwiperConfig?.breakpoints}
          loop={enableLoop}
          autoHeight={isFleet}
          allowTouchMove={canNavigate}
          touchRatio={isFleet ? 0.35 : 1}
          speed={isFleet ? 600 : 450}
          keyboard={{ enabled: true }}
          onSwiper={handleSwiperInit}
          className={`${swiperClassName} w-full`}
        >
          {slides.map((slide, index) => {
            return (
              <SwiperSlide
                key={`slider-item-${index}-${slide?.id ?? "slide"}`}
                className={`${context === "fleetPage" ? "fleet-swiper-slide" : ""} !flex !h-auto`}
              >
                <div
                  className={`relative w-full ${
                    context === "fleetPage" ? "fleet-slide isolate" : ""
                  }`}
                >
                  {renderSlideContent(slide)}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
}
